/**
 * Run replay capture via MediaRecorder over `canvas.captureStream()`.
 * Output is a self-contained WebM (VP9 where available, VP8 otherwise).
 *
 * Lifecycle: start(canvas) at run begin, stop() at end. stop() resolves
 * to a Blob URL the caller can use as href for a download link.
 *
 * Safari note: MediaRecorder is supported in Safari 14.1+ but only emits
 * MP4 (`video/mp4; codecs="avc1"`). We probe the mime types in priority
 * order so the feature degrades cleanly when nothing is supported.
 */

const MIME_CANDIDATES = [
  'video/webm;codecs="vp9,opus"',
  'video/webm;codecs="vp8,opus"',
  "video/webm",
  'video/mp4;codecs="avc1,mp4a.40.2"',
  "video/mp4",
];

export class ReplayRecorder {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private mime = "";
  private startedAt = 0;
  private hardStopTimer = 0;

  static isSupported(): boolean {
    return typeof MediaRecorder !== "undefined" && pickMime() !== "";
  }

  start(canvas: HTMLCanvasElement, fps = 30, maxDurationS = 60): boolean {
    this.stop().catch(() => {});
    if (!ReplayRecorder.isSupported()) return false;
    try {
      const stream = canvas.captureStream(fps);
      this.mime = pickMime();
      // Bit-rate kept modest so a 60s clip stays under ~20MB even at 1080p.
      this.recorder = new MediaRecorder(stream, {
        mimeType: this.mime,
        videoBitsPerSecond: 2_500_000,
      });
      this.chunks = [];
      this.recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this.chunks.push(e.data);
      };
      this.recorder.start(1000); // flush a chunk per second
      this.startedAt = performance.now();
      // Safety cap so the user can't accumulate a 30-minute Practice replay.
      this.hardStopTimer = window.setTimeout(() => this.stop(), maxDurationS * 1000);
      return true;
    } catch {
      this.recorder = null;
      return false;
    }
  }

  /**
   * Returns a Blob (or null if nothing recorded). Caller is responsible for
   * createObjectURL + cleanup.
   */
  async stop(): Promise<Blob | null> {
    if (this.hardStopTimer) {
      clearTimeout(this.hardStopTimer);
      this.hardStopTimer = 0;
    }
    if (!this.recorder) return null;
    if (this.recorder.state === "inactive") {
      this.recorder = null;
      return this.assemble();
    }
    return new Promise((resolve) => {
      const rec = this.recorder;
      if (!rec) {
        resolve(null);
        return;
      }
      rec.onstop = () => {
        this.recorder = null;
        resolve(this.assemble());
      };
      try {
        rec.stop();
      } catch {
        resolve(null);
      }
    });
  }

  private assemble(): Blob | null {
    if (this.chunks.length === 0) return null;
    const blob = new Blob(this.chunks, { type: this.mime });
    this.chunks = [];
    return blob;
  }

  extension(): string {
    return this.mime.startsWith("video/mp4") ? "mp4" : "webm";
  }
}

function pickMime(): string {
  if (typeof MediaRecorder === "undefined") return "";
  const isSupported = (m: string) => {
    try {
      return MediaRecorder.isTypeSupported(m);
    } catch {
      return false;
    }
  };
  return MIME_CANDIDATES.find(isSupported) ?? "";
}
